import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'merge', label: 'Merge Intervals' },
  { key: 'insert', label: 'Insert Interval' },
  { key: 'nonoverlap', label: 'Non-overlapping' },
  { key: 'meetingrooms2', label: 'Meeting Rooms II' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const IntervalBars = ({ label, intervals, min, max, getBarState }) => {
  const range = (max - min) || 1;
  const pct = v => ((v - min) / range) * 100;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {intervals.map(([s, e], i) => {
          const st = getBarState ? getBarState(i) : {};
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 58, fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)', flexShrink: 0, textAlign: 'right' }}>[{s},{e}]</span>
              <div style={{ position: 'relative', flex: 1, height: 20, background: 'var(--bg3)', borderRadius: 6, minWidth: 160 }}>
                <div style={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${pct(s)}%`, width: `${Math.max(pct(e) - pct(s), 1.5)}%`,
                  borderRadius: 6,
                  background: st.bg || `linear-gradient(90deg,${V1}55,${V2}35)`,
                  border: `1.5px solid ${st.border || `${V1}50`}`,
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 280 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const parseIntervals = str => str.split(';').map(pair => pair.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v))).filter(p => p.length === 2);
const rangeOf = intervals => {
  let min = Infinity, max = -Infinity;
  intervals.forEach(([s, e]) => { min = Math.min(min, s); max = Math.max(max, e); });
  return { min: min === Infinity ? 0 : min, max: max === -Infinity ? 1 : max };
};

/* ─── Merge Intervals ─────────────────────────────────────────────── */
const MergeMode = () => {
  const [input, setInput] = useState('1,3;2,6;8,10;15,18');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const raw = parseIntervals(input);
    if (raw.length === 0 || raw.length > 8) return;
    const sorted = [...raw].sort((a, b) => a[0] - b[0]);
    const built = [{ merged: [], note: `Sorted by start: ${sorted.map(iv => `[${iv[0]},${iv[1]}]`).join(', ')}.` }];
    const merged = [];
    for (const [s, e] of sorted) {
      if (merged.length > 0 && s <= merged[merged.length - 1][1]) {
        const last = merged[merged.length - 1];
        const newEnd = Math.max(last[1], e);
        built.push({ merged: [...merged.slice(0, -1), [last[0], newEnd]], touch: merged.length - 1, note: `[${s},${e}] overlaps the last merged interval [${last[0]},${last[1]}] (${s} ≤ ${last[1]}) — extend it to [${last[0]},${newEnd}].` });
        merged[merged.length - 1] = [last[0], newEnd];
      } else {
        merged.push([s, e]);
        built.push({ merged: [...merged], touch: merged.length - 1, note: `[${s},${e}] starts after the last merged interval ends — no overlap. Add it as a new interval.` });
      }
    }
    built.push({ merged: [...merged], done: true, note: `Final merged result: ${merged.map(iv => `[${iv[0]},${iv[1]}]`).join(', ')}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const raw = parseIntervals(input);
  const { min, max } = rangeOf(raw);
  const getBarState = i => (cur && cur.touch === i) ? { bg: `linear-gradient(90deg,${V1},${V2})`, border: V1 } : {};

  const code = {
    lines: [
      'int[][] merge(int[][] intervals) {',
      '    Arrays.sort(intervals, (a, b) -> a[0] - b[0]);',
      '    List<int[]> merged = new ArrayList<>();',
      '    for (int[] iv : intervals) {',
      '        if (!merged.isEmpty() && iv[0] <= merged.get(merged.size()-1)[1]) {',
      '            merged.get(merged.size()-1)[1] =',
      '                Math.max(merged.get(merged.size()-1)[1], iv[1]);',
      '        } else {',
      '            merged.add(iv);',
      '        }',
      '    }',
      '    return merged.toArray(new int[0][]);',
      '}',
    ],
    active: !cur || cur.done ? -1 : (cur.touch !== undefined && cur.merged.length > 0 && cur.note.includes('overlaps')) ? 5 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <IntervalBars label="intervals (after sorting by start)" intervals={cur && cur.merged.length ? cur.merged : (raw.length ? [...raw].sort((a, b) => a[0] - b[0]) : [])} min={min} max={max} getBarState={getBarState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,3;2,6;8,10;15,18" />
          <button onClick={run} style={smallBtn(V1)}>Merge</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter intervals as "start,end" pairs separated by ";" (max 8), then Merge.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Merge Intervals" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Insert Interval ─────────────────────────────────────────────── */
const InsertMode = () => {
  const [input, setInput] = useState('1,3;6,9');
  const [newInterval, setNewInterval] = useState('2,5');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const intervals = parseIntervals(input);
    const newIv = newInterval.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (intervals.length === 0 || newIv.length !== 2) return;
    const built = [];
    const result = [];
    let [ns, ne] = newIv;
    let i = 0;
    while (i < intervals.length && intervals[i][1] < ns) {
      result.push(intervals[i]);
      built.push({ result: [...result], pending: [ns, ne], note: `[${intervals[i][0]},${intervals[i][1]}] ends before the new interval starts — add it as-is, unchanged.`, touch: intervals[i] });
      i++;
    }
    while (i < intervals.length && intervals[i][0] <= ne) {
      const before = [ns, ne];
      ns = Math.min(ns, intervals[i][0]);
      ne = Math.max(ne, intervals[i][1]);
      built.push({ result: [...result], pending: [ns, ne], note: `[${intervals[i][0]},${intervals[i][1]}] overlaps the growing new interval [${before[0]},${before[1]}] — merge into [${ns},${ne}].`, touch: intervals[i] });
      i++;
    }
    result.push([ns, ne]);
    built.push({ result: [...result], pending: null, note: `The merged new interval [${ns},${ne}] is now finalized and added.` });
    while (i < intervals.length) {
      result.push(intervals[i]);
      built.push({ result: [...result], pending: null, note: `[${intervals[i][0]},${intervals[i][1]}] starts after the merged interval ends — add it as-is.`, touch: intervals[i] });
      i++;
    }
    built.push({ result: [...result], pending: null, done: true, note: `Final result: ${result.map(iv => `[${iv[0]},${iv[1]}]`).join(', ')}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const intervals = parseIntervals(input);
  const newIv = newInterval.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
  const allShown = cur ? [...cur.result, ...(cur.pending ? [cur.pending] : [])] : [...intervals, ...(newIv.length === 2 ? [newIv] : [])];
  const { min, max } = rangeOf(allShown.length ? allShown : intervals);
  const getBarState = ([s, e]) => {
    if (!cur) return {};
    if (cur.pending && s === cur.pending[0] && e === cur.pending[1]) return { bg: `linear-gradient(90deg,${GREEN},#27AE60)`, border: GREEN };
    return {};
  };

  const code = {
    lines: [
      'int[][] insert(int[][] intervals, int[] newInterval) {',
      '    List<int[]> result = new ArrayList<>();',
      '    int i = 0, n = intervals.length;',
      '    while (i < n && intervals[i][1] < newInterval[0]) {',
      '        result.add(intervals[i++]);',
      '    }',
      '    while (i < n && intervals[i][0] <= newInterval[1]) {',
      '        newInterval[0] = Math.min(newInterval[0], intervals[i][0]);',
      '        newInterval[1] = Math.max(newInterval[1], intervals[i][1]);',
      '        i++;',
      '    }',
      '    result.add(newInterval);',
      '    while (i < n) result.add(intervals[i++]);',
      '    return result.toArray(new int[0][]);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 12 : cur.note.includes('ends before') ? 4 : cur.note.includes('overlaps') ? 7 : cur.note.includes('finalized') ? 11 : 12,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <IntervalBars label="result so far (+ pending new interval)" intervals={allShown} min={min} max={max} getBarState={([s, e]) => getBarState([s, e])} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,3;6,9" />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>new</span>
          <input value={newInterval} onChange={e => { setNewInterval(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 70 }} placeholder="2,5" />
          <button onClick={run} style={smallBtn(V1)}>Insert</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter sorted, non-overlapping intervals plus a new interval, then Insert.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Insert Interval" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Non-overlapping Intervals ───────────────────────────────────── */
const NonOverlapMode = () => {
  const [input, setInput] = useState('1,2;2,3;3,4;1,3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const raw = parseIntervals(input);
    if (raw.length === 0 || raw.length > 8) return;
    const sorted = [...raw].sort((a, b) => a[1] - b[1]);
    const built = [{ note: `Sorted by END: ${sorted.map(iv => `[${iv[0]},${iv[1]}]`).join(', ')}.` }];
    let removed = 0, lastEnd = -Infinity;
    sorted.forEach(([s, e]) => {
      if (s >= lastEnd) {
        built.push({ touch: [s, e], kept: true, removed, note: `[${s},${e}]: start (${s}) ≥ last kept end (${lastEnd === -Infinity ? '−∞' : lastEnd}) — KEEP it. New last end = ${e}.` });
        lastEnd = e;
      } else {
        removed++;
        built.push({ touch: [s, e], kept: false, removed, note: `[${s},${e}]: start (${s}) < last kept end (${lastEnd}) — overlaps! REMOVE it. removed = ${removed}.` });
      }
    });
    built.push({ done: true, removed, note: `Final answer: ${removed} interval${removed === 1 ? '' : 's'} removed.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const raw = parseIntervals(input);
  const sorted = [...raw].sort((a, b) => a[1] - b[1]);
  const { min, max } = rangeOf(raw);
  const getBarState = ([s, e]) => {
    if (!cur || !cur.touch) return {};
    if (cur.touch[0] === s && cur.touch[1] === e) {
      return cur.kept ? { bg: `linear-gradient(90deg,${GREEN},#27AE60)`, border: GREEN } : { bg: `linear-gradient(90deg,${RED},#c0293a)`, border: RED };
    }
    return {};
  };

  const code = {
    lines: [
      'int eraseOverlapIntervals(int[][] intervals) {',
      '    Arrays.sort(intervals, (a, b) -> a[1] - b[1]);',
      '    int removed = 0;',
      '    int lastEnd = Integer.MIN_VALUE;',
      '    for (int[] iv : intervals) {',
      '        if (iv[0] >= lastEnd) {',
      '            lastEnd = iv[1];',
      '        } else {',
      '            removed++;',
      '        }',
      '    }',
      '    return removed;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 11 : cur.kept ? 6 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <IntervalBars label="intervals (sorted by end)" intervals={sorted} min={min} max={max} getBarState={getBarState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,2;2,3;3,4;1,3" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter intervals (max 8), then Run — green = kept, red = removed.` : cur.note}
          {cur && cur.removed !== undefined && <> <Chip color={RED}>removed = {cur.removed}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Non-overlapping Intervals (Greedy)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Meeting Rooms II ────────────────────────────────────────────── */
const MeetingRooms2Mode = () => {
  const [input, setInput] = useState('0,30;5,10;15,20');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const raw = parseIntervals(input);
    if (raw.length === 0 || raw.length > 8) return;
    const starts = raw.map(iv => iv[0]).sort((a, b) => a - b);
    const ends = raw.map(iv => iv[1]).sort((a, b) => a - b);
    const built = [{ note: `Sorted starts: [${starts.join(', ')}]. Sorted ends: [${ends.join(', ')}].` }];
    let s = 0, e = 0, rooms = 0, maxRooms = 0;
    const n = starts.length;
    while (s < n) {
      if (starts[s] < ends[e]) {
        rooms++;
        maxRooms = Math.max(maxRooms, rooms);
        built.push({ s, e, rooms, maxRooms, note: `start[${s}]=${starts[s]} < end[${e}]=${ends[e]} — a new meeting begins before the earliest one ends. rooms=${rooms}.` });
        s++;
      } else {
        rooms--;
        built.push({ s, e, rooms, maxRooms, note: `start[${s}]=${starts[s]} ≥ end[${e}]=${ends[e]} — a room frees up before (or exactly as) the next meeting starts. rooms=${rooms}.` });
        e++;
      }
    }
    built.push({ done: true, maxRooms, note: `Final answer: ${maxRooms} room${maxRooms === 1 ? '' : 's'} needed at peak.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const raw = parseIntervals(input);
  const { min, max } = rangeOf(raw);
  const getBarState = () => ({});

  const code = {
    lines: [
      'int minMeetingRooms(int[][] intervals) {',
      '    int n = intervals.length;',
      '    int[] starts = new int[n], ends = new int[n];',
      '    for (int i = 0; i < n; i++) { starts[i] = intervals[i][0]; ends[i] = intervals[i][1]; }',
      '    Arrays.sort(starts); Arrays.sort(ends);',
      '    int s = 0, e = 0, rooms = 0, maxRooms = 0;',
      '    while (s < n) {',
      '        if (starts[s] < ends[e]) {',
      '            rooms++; maxRooms = Math.max(maxRooms, rooms); s++;',
      '        } else {',
      '            rooms--; e++;',
      '        }',
      '    }',
      '    return maxRooms;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 13 : cur.note.includes('begins before') ? 8 : 10,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <IntervalBars label="meetings" intervals={raw} min={min} max={max} getBarState={getBarState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="0,30;5,10;15,20" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter meetings (max 8), then Run — two-pointer sweep over sorted starts and ends.` : cur.note}
          {cur && cur.rooms !== undefined && <> <Chip color={TEAL}>rooms in use = {cur.rooms}</Chip><Chip color={V2}>peak = {cur.maxRooms}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Meeting Rooms II (Two-Pointer Sweep)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const s1 = Math.floor(Math.random() * 10);
  const e1 = s1 + 1 + Math.floor(Math.random() * 6);
  const s2 = Math.floor(Math.random() * 16);
  const e2 = s2 + 1 + Math.floor(Math.random() * 6);
  const overlap = s1 < e2 && s2 < e1;
  const correct = overlap ? 'overlap' : 'nooverlap';
  return { s1, e1, s2, e2, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'overlap', label: 'They OVERLAP' },
    { key: 'nooverlap', label: 'They do NOT overlap' },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        Two intervals: [{scenario.s1}, {scenario.e1}] and [{scenario.s2}, {scenario.e2}]. Do they overlap?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>
            The overlap test: start1 &lt; end2 AND start2 &lt; end1.
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const IntervalsPlayground = () => {
  const [mode, setMode] = useState('merge');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'merge' && <MergeMode />}
      {mode === 'insert' && <InsertMode />}
      {mode === 'nonoverlap' && <NonOverlapMode />}
      {mode === 'meetingrooms2' && <MeetingRooms2Mode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default IntervalsPlayground;
